/**
 * Single presentation owner for user-started operations.
 * Dialog-owned ops must not emit helper/interceptor toasts.
 */

import {
  OPERATION_PHASE,
  STEP_STATUS,
  createClientOperationId,
} from "./types";

/**
 * @typedef {object} OperationStep
 * @property {string} key
 * @property {string} label
 * @property {string} status
 * @property {string} [detail]
 * @property {object} [resultRef]
 * @property {number} [revision]
 */

/**
 * @typedef {object} ClientOperation
 * @property {string} clientOperationId
 * @property {string} operationKey
 * @property {string} [title]
 * @property {string} presentationOwner
 * @property {string} phase
 * @property {OperationStep[]} steps
 * @property {number} revision
 * @property {string} [serverOperationId]
 * @property {string} [attemptId]
 * @property {string|number|null} [resourceId]
 * @property {string|number|null} [companyId]
 * @property {object} [result]
 * @property {string} [summary]
 * @property {boolean} visible
 * @property {number} updatedAt
 */

class OperationCoordinator {
  constructor() {
    /** @type {Map<string, ClientOperation>} */
    this.operations = new Map();
    this.listeners = new Set();
    /** Active dialog-owned operation id (at most one focused modal). */
    this.activeDialogId = null;
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  notify() {
    const snap = this.getSnapshot();
    this.listeners.forEach((fn) => {
      try {
        fn(snap);
      } catch (_) {
        /* ignore */
      }
    });
  }

  getSnapshot() {
    return {
      activeDialogId: this.activeDialogId,
      operations: Array.from(this.operations.values()).map((o) => ({
        ...o,
        steps: o.steps.map((s) => ({ ...s })),
      })),
    };
  }

  hasActiveDialogOwner() {
    if (!this.activeDialogId) return false;
    const op = this.operations.get(this.activeDialogId);
    return Boolean(op && op.presentationOwner === "dialog" && op.visible);
  }

  isDialogOwned(clientOperationId) {
    const op = this.operations.get(clientOperationId);
    return Boolean(op && op.presentationOwner === "dialog");
  }

  getOperation(clientOperationId) {
    return this.operations.get(clientOperationId) || null;
  }

  /**
   * Start a user action. Prefer presentationOwner: 'dialog' for multi-step.
   * @param {object} opts
   */
  start(opts = {}) {
    const attemptId =
      opts.attemptId ||
      (typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `a-${Date.now()}`);
    const clientOperationId =
      opts.clientOperationId ||
      createClientOperationId({
        operationKey: opts.operationKey,
        resourceId: opts.resourceId,
        companyId: opts.companyId,
        userId: opts.userId,
        attemptId,
      });

    const steps = (opts.steps || []).map((s) => ({
      key: s.key,
      label: s.label,
      status: s.status || STEP_STATUS.PENDING,
      detail: s.detail,
      resultRef: s.resultRef,
      revision: 0,
    }));

    /** @type {ClientOperation} */
    const op = {
      clientOperationId,
      operationKey: opts.operationKey || "operation",
      title: opts.title || opts.operationKey || "Operation",
      presentationOwner: opts.presentationOwner || "dialog",
      phase: opts.phase || OPERATION_PHASE.RUNNING,
      steps,
      revision: 0,
      serverOperationId: opts.serverOperationId,
      attemptId,
      resourceId: opts.resourceId ?? null,
      companyId: opts.companyId ?? null,
      result: null,
      summary: null,
      visible: true,
      updatedAt: Date.now(),
    };

    this.operations.set(clientOperationId, op);
    if (op.presentationOwner === "dialog") {
      this.activeDialogId = clientOperationId;
    }
    this.notify();
    return op;
  }

  /**
   * Update a step only from a confirmed server/API result — never from timers.
   * @param {string} clientOperationId
   * @param {string} stepKey
   * @param {object} patch
   * @param {number} [patch.revision] monotonic; older revisions ignored
   */
  updateStep(clientOperationId, stepKey, patch = {}) {
    const op = this.operations.get(clientOperationId);
    if (!op) return null;
    const step = op.steps.find((s) => s.key === stepKey);
    if (!step) return null;

    const nextRev =
      patch.revision != null ? Number(patch.revision) : step.revision + 1;
    if (nextRev < step.revision) {
      return op;
    }

    if (patch.status != null) step.status = patch.status;
    if (patch.detail !== undefined) step.detail = patch.detail;
    if (patch.resultRef !== undefined) step.resultRef = patch.resultRef;
    if (patch.label != null) step.label = patch.label;
    step.revision = nextRev;
    op.revision += 1;
    op.updatedAt = Date.now();
    this.notify();
    return op;
  }

  /**
   * Apply a server status payload with monotonic revision.
   * @param {string} clientOperationId
   * @param {object} payload
   */
  applyServerStatus(clientOperationId, payload = {}) {
    const op = this.operations.get(clientOperationId);
    if (!op) return null;
    const incomingRev = Number(payload.revision ?? payload.sequence ?? -1);
    if (incomingRev >= 0 && incomingRev < op.revision) {
      return op;
    }
    if (payload.serverOperationId) {
      op.serverOperationId = payload.serverOperationId;
    }
    if (Array.isArray(payload.steps)) {
      payload.steps.forEach((s) => {
        if (!s?.key) return;
        const existing = op.steps.find((x) => x.key === s.key);
        if (existing) {
          this.updateStep(clientOperationId, s.key, {
            status: s.status,
            detail: s.detail,
            resultRef: s.result_ref || s.resultRef,
            revision: s.revision != null ? s.revision : existing.revision + 1,
            label: s.label || s.description,
          });
        } else {
          op.steps.push({
            key: s.key,
            label: s.label || s.description || s.key,
            status: s.status || STEP_STATUS.PENDING,
            detail: s.detail,
            resultRef: s.result_ref || s.resultRef,
            revision: s.revision || 0,
          });
        }
      });
    }
    if (incomingRev >= 0) op.revision = Math.max(op.revision, incomingRev);
    op.updatedAt = Date.now();
    this.notify();
    return op;
  }

  /**
   * Move to durable result phase. Does not auto-close.
   */
  complete(clientOperationId, result = {}) {
    const op = this.operations.get(clientOperationId);
    if (!op) return null;
    op.phase = OPERATION_PHASE.RESULT;
    op.result = result;
    op.summary = result.summary || op.summary;
    op.updatedAt = Date.now();
    op.revision += 1;
    this.notify();
    return op;
  }

  /** Hide UI without claiming server cancel. */
  hide(clientOperationId) {
    const op = this.operations.get(clientOperationId);
    if (!op) return null;
    op.visible = false;
    op.phase = OPERATION_PHASE.HIDDEN;
    if (this.activeDialogId === clientOperationId) {
      this.activeDialogId = null;
    }
    op.updatedAt = Date.now();
    this.notify();
    try {
      // Lazy require avoids circular init with toastQueue.
      // eslint-disable-next-line @typescript-eslint/no-var-requires -- Keep the coordinator/toastQueue cycle lazy.
      const { flushToastQueue } = require("./toastQueue");
      flushToastQueue();
    } catch (_) {
      /* ignore */
    }
    return op;
  }

  reopen(clientOperationId) {
    const op = this.operations.get(clientOperationId);
    if (!op) return null;
    op.visible = true;
    if (op.phase === OPERATION_PHASE.HIDDEN) {
      op.phase =
        op.result != null ? OPERATION_PHASE.RESULT : OPERATION_PHASE.RUNNING;
    }
    if (op.presentationOwner === "dialog") {
      this.activeDialogId = clientOperationId;
    }
    op.updatedAt = Date.now();
    this.notify();
    return op;
  }

  /** Explicit dismiss after user action (Lukk). Clears dialog ownership. */
  dismiss(clientOperationId) {
    const op = this.operations.get(clientOperationId);
    if (!op) return;
    if (this.activeDialogId === clientOperationId) {
      this.activeDialogId = null;
    }
    this.operations.delete(clientOperationId);
    this.notify();
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires -- Keep the coordinator/toastQueue cycle lazy.
      const { flushToastQueue } = require("./toastQueue");
      flushToastQueue();
    } catch (_) {
      /* ignore */
    }
  }

  clearCompanyContext(companyId) {
    const cid = companyId != null ? String(companyId) : null;
    for (const [id, op] of this.operations.entries()) {
      if (cid == null || String(op.companyId) === cid) {
        if (this.activeDialogId === id) this.activeDialogId = null;
        this.operations.delete(id);
      }
    }
    this.notify();
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires -- Keep the coordinator/toastQueue cycle lazy.
      const { clearAllToasts } = require("./toastQueue");
      clearAllToasts();
    } catch (_) {
      /* ignore */
    }
  }

  /** Full SPA logout / session teardown — clear every operation + toast queue. */
  clearSession() {
    this.clearCompanyContext(null);
  }
}

let singleton = null;

export function getOperationCoordinator() {
  if (!singleton) singleton = new OperationCoordinator();
  return singleton;
}

/** @deprecated use getOperationCoordinator */
export function getCoordinator() {
  return getOperationCoordinator();
}

export function __resetOperationCoordinatorForTests() {
  singleton = new OperationCoordinator();
  return singleton;
}

export { OperationCoordinator };
